import { PlusOutlined } from '@ant-design/icons';
import { type CSSProperties, useRef } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import LabelWithProperty from '@/components/BaseLabel/LabelWithProperty';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import { ADD_TAG_LABEL, NOT_TAGGED_LABEL } from '@/configs/reserved-word';
import useColor from '@/hooks/domain/useColor';
import useKeymap from '@/hooks/pure/useKeymap';
import useDropdownStore from '@/stores/pure/DropdownStore';
import useTagStore from '@/stores/TagStore';
import feedbackUtils from '@/utils/feedback';

import { ValueWrapper } from '../../FeedbackDetail/PropertyArea/ValueWrapper';
import TagSelectorModal from './TagSelectorModal';
interface Props {
  selectedTags?: string[];
  toggleItem: (tag: string) => void;
  listStyle?: CSSProperties;
  suggestions?: string[];
  name: string;
  isCreate?: boolean;
  showFull?: boolean;
  setShowFull?: (showFull: boolean) => void;
  isEditable?: boolean;
}

const TagSelector = (props: Props) => {
  const { setCurrDropdown, shortcutEnabled } = useDropdownStore(
    ({ currDropdown, setCurrDropdown, shortcutEnabled }) => ({
      currDropdown,
      setCurrDropdown,
      shortcutEnabled,
    }),
  );
  const { selectedTags } = props;
  const { tagMap } = useTagStore((state) => ({
    tagMap: state.tagMap,
  }));

  const toggleTagSelectModal = () => {
    props.isEditable && setCurrDropdown(props.name);
  };

  useKeymap('KeyT', () => {
    shortcutEnabled && toggleTagSelectModal();
  });

  const triggerRef = useRef<HTMLDivElement>(null);
  const labels =
    selectedTags !== undefined && selectedTags.length > 0
      ? selectedTags.filter((key) => tagMap[key])
      : [];
  const color = useColor();

  return (
    <Style>
      <Flex
        d="row"
        g="4px 1px"
        style={{
          flexWrap: 'wrap',
          maxWidth: '400px',
          ...props.listStyle,
        }}
        ref={triggerRef}
      >
        <Flex w="wrap" g={4} f={1}>
          {!props.showFull ? (
            <FoldedTagList
              labels={labels}
              onClick={toggleTagSelectModal}
              isEditable={props.isEditable}
              setShowFull={props.setShowFull}
            />
          ) : (
            <>
              <UnfoldedTagList
                labels={labels}
                onClick={toggleTagSelectModal}
                isEditable={props.isEditable}
              />
              {props.isEditable && (
                <PropertyValue
                  icon={
                    <PlusOutlined
                      style={{ color: color.gray600, fontSize: '12px' }}
                    />
                  }
                  value={ADD_TAG_LABEL}
                  onClick={toggleTagSelectModal}
                  style={{ color: color.gray600 }}
                />
              )}
            </>
          )}
        </Flex>
        {props.isEditable && (
          <TagSelectorModal {...props} triggerEl={triggerRef.current} />
        )}
      </Flex>
    </Style>
  );
};

const Style = styled(Flex).attrs({
  d: 'column',
})`
  position: relative;
`;

export default TagSelector;

export const FoldedTagList = (props: {
  labels: string[];
  onClick?: () => void;
  setShowFull?: (showFull: boolean) => void;
  isEditable?: boolean;
}) => {
  const color = useColor();
  const tagMap = useTagStore((state) => state.tagMap);
  const MAX_TAG_COUNT = 2;

  const slicedLabels = props.labels.slice(0, MAX_TAG_COUNT);

  if (props.labels.length === 0) {
    return <EmptyTagList onClick={props.onClick} />;
  }
  return (
    <div className="flex gap-1 w-full overflow-ellipsis">
      {slicedLabels?.map((key: string) => {
        const tag = tagMap[key];
        const fullTag = feedbackUtils.getTagHTML(tagMap, key);
        return (
          <ValueWrapper key={tag.alias} className="py-0.5 px-1.5">
            <TooltipV2 tooltipNode={fullTag}>
              <Flex g={4} className="items-center">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    backgroundColor: color.profile[tag.color - 1],
                  }}
                ></div>
                <Typo type="label3" ellipsis className="max-w-30">
                  {tag.name}
                </Typo>
              </Flex>
            </TooltipV2>
          </ValueWrapper>
        );
      })}
      {props.labels?.length > MAX_TAG_COUNT && (
        <PropertyValue
          value={`+${props.labels.length - MAX_TAG_COUNT} more`}
          onClick={() => props.setShowFull?.(true)}
          style={{ color: color.gray500 }}
        />
      )}
    </div>
  );
};

export const UnfoldedTagList = (props: {
  labels: string[];
  isEditable?: boolean;
  onClick?: () => void;
}) => {
  const color = useColor();
  const tagMap = useTagStore((state) => state.tagMap);

  if (props.labels.length === 0) {
    return <EmptyTagList onClick={props.onClick} />;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {props.labels?.map((key: string) => {
        const tag = tagMap[key];
        const fullTag = feedbackUtils.getTagHTML(tagMap, key);
        return (
          <LabelWithProperty
            key={tag.alias}
            color={color.profile[tag.color - 1]}
            name={
              <span
                dangerouslySetInnerHTML={{
                  __html: fullTag,
                }}
              />
            }
            onClick={props.onClick}
          />
        );
      })}
    </div>
  );
};

export const EmptyTagList = (props: { onClick?: () => void }) => {
  return <PropertyValue value={NOT_TAGGED_LABEL} onClick={props.onClick} />;
};
